import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDeleteTemplate, useReportTemplates } from '../api/report-templates.js';
import { useMoveItem } from '../api/reorder.js';
import {
  useCreateFolder,
  useDeleteFolder,
  useFolders,
  useMoveFolder,
  useRenameFolder,
  type Folder,
} from '../api/folders.js';
import { useApi } from '../api/client.js';
import type { ReportTemplateSummary } from '../api/types.js';
import { TemplateDetail } from './reports/TemplateDetail.js';
import { NewTemplateDialog } from './reports/NewTemplateDialog.js';
import { FolderTree } from './reports/FolderTree.js';
import { MoveToFolderDialog } from './reports/MoveToFolderDialog.js';
import { PromptDialog } from '../components/dialogs/PromptDialog.js';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog.js';
import { useListSidebar, useListSidebarWidth } from '../shell/useAppChrome.js';
import { DrillBreadcrumb } from '../shell/drill-through.js';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Reports — Jasper's replacement.
 *
 * Left: folder tree with templates rendered as leaves inside their
 * folders (Jasper / SSRS / Crystal pattern). Every folder and template
 * row has a hover action menu (rename, move, delete, clone).
 *
 * Right: the selected template's detail pane.
 */
export function TemplatesPage(): JSX.Element {
  const templates = useReportTemplates();
  const foldersQ = useFolders('reports');
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(urlId ?? null);
  const [showNew, setShowNew] = useState(false);
  const listSidebar = useListSidebar('reports');
  const listSidebarWidth = useListSidebarWidth('templates');
  const api = useApi();
  const qc = useQueryClient();

  // Sidebar toolbar state
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggleFolder(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Dialog state.
  const [moveTarget, setMoveTarget] = useState<MoveTargetState | null>(null);
  const [promptDialog, setPromptDialog] = useState<PromptState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState | null>(null);

  useEffect(() => {
    if (urlId && urlId !== selected) setSelected(urlId);
  }, [urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectTemplate(id: string | null): void {
    setSelected(id);
    navigate(id ? `/templates/${id}${window.location.search}` : '/templates');
  }

  // ── Mutations
  const renameFolderM = useRenameFolder();
  const moveFolderM = useMoveFolder();
  const deleteFolderM = useDeleteFolder();
  const createFolderM = useCreateFolder();
  const deleteTemplateM = useDeleteTemplate();
  const moveItem = useMoveItem();

  // Template-update mutation is keyed per-template by the hook, so we
  // bypass it here and hit the endpoint directly — less scope-creep
  // than refactoring useUpdateTemplateSettings.
  const updateTemplateSettings = async (
    id: string,
    patch: Record<string, unknown>,
  ): Promise<void> => {
    await api.fetch(`/report-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    void qc.invalidateQueries({ queryKey: ['report-templates'] });
  };

  // ── Action handlers
  function handleRenameFolder(f: Folder): void {
    setPromptDialog({
      title: 'Rename folder',
      label: 'Folder name',
      initialValue: f.name,
      submitLabel: 'Rename',
      onSubmit: async (newName) => {
        await renameFolderM.mutateAsync({ id: f.id, name: newName });
        setPromptDialog(null);
      },
    });
  }
  function handleDeleteFolder(f: Folder): void {
    setConfirmDialog({
      title: 'Delete folder',
      message: (
        <>
          Delete <strong>{f.name}</strong>? Templates inside this folder will be moved to top level;
          any nested folders are deleted recursively. This action cannot be undone.
        </>
      ),
      confirmLabel: 'Delete folder',
      onConfirm: async () => {
        await deleteFolderM.mutateAsync(f.id);
        setConfirmDialog(null);
      },
    });
  }
  function handleNewFolder(parent: Folder | null): void {
    setPromptDialog({
      title: parent ? `New folder inside "${parent.name}"` : 'New top-level folder',
      label: 'Folder name',
      placeholder: 'e.g. Finance',
      submitLabel: 'Create',
      onSubmit: async (name) => {
        await createFolderM.mutateAsync({
          name,
          type: 'reports',
          parentId: parent?.id ?? null,
        });
        setPromptDialog(null);
      },
    });
  }

  function handleRenameTemplate(t: ReportTemplateSummary): void {
    const jasperTag = t.name.match(/\s\[Jasper #\d+\]$/)?.[0] ?? '';
    setPromptDialog({
      title: 'Rename template',
      description: jasperTag
        ? `The "${jasperTag.trim()}" suffix will be preserved so the Jasper origin stays traceable.`
        : undefined,
      label: 'Template name',
      initialValue: stripJasperTag(t.name),
      submitLabel: 'Rename',
      onSubmit: async (newName) => {
        await updateTemplateSettings(t.id, { name: `${newName}${jasperTag}` });
        setPromptDialog(null);
      },
    });
  }
  function handleDeleteTemplate(t: ReportTemplateSummary): void {
    const displayName = stripJasperTag(t.name);
    setConfirmDialog({
      title: 'Delete template',
      message: (
        <>
          Delete <strong>{displayName}</strong>? Every version of the template and every{' '}
          <strong>Report</strong> (instance) bound to it will also be deleted. This action cannot be
          undone.
        </>
      ),
      confirmLabel: 'Delete template',
      requireTyping: displayName.length <= 60 ? displayName : undefined,
      onConfirm: async () => {
        await deleteTemplateM.mutateAsync(t.id);
        if (selected === t.id) selectTemplate(null);
        setConfirmDialog(null);
      },
    });
  }
  function handleCloneTemplate(t: ReportTemplateSummary): void {
    setPromptDialog({
      title: 'Clone as report',
      description:
        'This creates a new Report (instance) backed by the same template. ' +
        'You can then set per-customer defaults, scheduling, and delivery on the report.',
      label: 'Report name',
      initialValue: stripJasperTag(t.name),
      submitLabel: 'Create report',
      onSubmit: async (name) => {
        await api.fetch('/reports', {
          method: 'POST',
          body: JSON.stringify({ templateId: t.id, name, folderId: t.folderId }),
        });
        void qc.invalidateQueries({ queryKey: ['reports'] });
        setPromptDialog(null);
      },
    });
  }

  function handleMoveFolder(f: Folder): void {
    setMoveTarget({ kind: 'folder', folder: f });
  }
  function handleMoveTemplate(t: ReportTemplateSummary): void {
    setMoveTarget({ kind: 'template', template: t });
  }
  function handleReorderTemplate(t: ReportTemplateSummary, direction: 'up' | 'down'): void {
    const all = templates.data ?? [];
    const siblings = all
      .filter((x) => (x.folderId ?? null) === (t.folderId ?? null))
      .map((x) => ({ id: x.id }));
    moveItem('templates', t.id, direction, siblings);
  }
  function handleReorderFolder(f: Folder, direction: 'up' | 'down'): void {
    const all = foldersQ.data ?? [];
    const siblings = all
      .filter((x) => (x.parentId ?? null) === (f.parentId ?? null) && x.type === f.type)
      .map((x) => ({ id: x.id }));
    moveItem('folders', f.id, direction, siblings);
  }
  async function handleMoveTargetChosen(newParentId: string | null): Promise<void> {
    if (!moveTarget) return;
    if (moveTarget.kind === 'folder') {
      await moveFolderM.mutateAsync({ id: moveTarget.folder.id, parentId: newParentId });
    } else {
      await updateTemplateSettings(moveTarget.template.id, { folderId: newParentId });
    }
    setMoveTarget(null);
  }

  // Pre-compute folder lookup for the move dialog.
  const foldersById = useMemo(() => {
    const map = new Map<string, Folder>();
    for (const f of foldersQ.data ?? []) map.set(f.id, f);
    return map;
  }, [foldersQ.data]);

  const totalTemplates = templates.data?.length ?? 0;

  return (
    <>
      <DrillBreadcrumb />

      <div
        className="split-layout"
        data-sidebar={listSidebar.mode}
        // Inline styles win over CSS rules; only emit when expanded
        // so the collapsed-state rule (--list-sidebar-width: 0) can
        // actually shrink the grid column.
        style={
          listSidebar.mode === 'expanded'
            ? ({ '--list-sidebar-width': `${listSidebarWidth.width}px` } as CSSProperties)
            : undefined
        }
      >
        <aside className="split-sidebar">
          <div className="split-sidebar-header">
            <span>Templates · {totalTemplates}</span>
            <div className="split-sidebar-header-actions">
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowNew(true)}
                title="New template"
                aria-label="New template"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M7 3v8M3 7h8" />
                </svg>
              </button>
              <button
                type="button"
                className="btn-icon"
                onClick={listSidebar.toggle}
                title="Collapse template list"
                aria-label="Collapse template list"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 4L6 8l4 4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="split-sidebar-toolbar">
            <div className="split-sidebar-search">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M13.5 13.5l-3-3" />
              </svg>
              <input
                type="search"
                placeholder="Search folders + templates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              {search && (
                <button
                  type="button"
                  className="btn-icon split-sidebar-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {templates.isLoading && (
            <div className="text-dim text-sm" style={{ padding: 'var(--space-3)' }}>
              Loading…
            </div>
          )}
          {templates.error && (
            <div className="alert alert--danger" style={{ margin: 'var(--space-3)' }}>
              Failed to load templates. Session may have expired.
            </div>
          )}
          {templates.data && totalTemplates === 0 && !templates.isLoading && (
            <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
              <div className="empty-state-icon">◰</div>
              <h3>No templates yet</h3>
              <p>Create your first report template from a dataset to get started.</p>
              <button
                className="btn btn--primary btn--sm"
                onClick={() => setShowNew(true)}
                type="button"
              >
                New Template
              </button>
            </div>
          )}

          <FolderTree
            folders={foldersQ.data ?? []}
            templates={templates.data ?? []}
            selectedTemplateId={selected}
            onSelectTemplate={selectTemplate}
            expanded={expanded}
            onToggle={toggleFolder}
            searchQuery={search}
            onRenameFolder={handleRenameFolder}
            onMoveFolder={handleMoveFolder}
            onDeleteFolder={handleDeleteFolder}
            onNewFolder={handleNewFolder}
            onReorderFolder={handleReorderFolder}
            onRenameTemplate={handleRenameTemplate}
            onMoveTemplate={handleMoveTemplate}
            onCloneTemplate={handleCloneTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onReorderTemplate={handleReorderTemplate}
          />
        </aside>

        {listSidebar.mode === 'expanded' && (
          <div
            className="split-sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize list panel"
            onDoubleClick={listSidebarWidth.reset}
            {...listSidebarWidth.bindResizer()}
            title="Drag to resize · double-click to reset"
          />
        )}

        <section>
          {listSidebar.mode === 'collapsed' && (
            <button
              type="button"
              className="split-sidebar-reopen"
              onClick={listSidebar.toggle}
              title="Open template list"
              aria-label="Open template list"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4l4 4-4 4" />
              </svg>
              Templates ({totalTemplates})
            </button>
          )}
          {selected ? (
            <TemplateDetail
              templateId={selected}
              onRenderStart={() => listSidebar.set('collapsed')}
            />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">◰</div>
              <h3>Select a template</h3>
              <p>
                Pick a template on the left to preview, edit its layout, or render. Every template
                is tenant-scoped, versioned, and audited; publishing creates an immutable new
                version and rollback is a one-click pointer swap.
              </p>
            </div>
          )}
        </section>
      </div>

      {showNew && (
        <NewTemplateDialog
          onClose={() => setShowNew(false)}
          onCreated={(t: ReportTemplateSummary) => {
            setShowNew(false);
            selectTemplate(t.id);
          }}
        />
      )}

      {moveTarget && (
        <MoveToFolderDialog
          sourceLabel={
            moveTarget.kind === 'folder'
              ? `folder "${moveTarget.folder.name}"`
              : `template "${stripJasperTag(moveTarget.template.name)}"`
          }
          folders={foldersQ.data ?? []}
          foldersById={foldersById}
          // For folders, disallow picking a descendant of itself.
          disallowId={moveTarget.kind === 'folder' ? moveTarget.folder.id : undefined}
          onCancel={() => setMoveTarget(null)}
          onChoose={handleMoveTargetChosen}
        />
      )}

      {promptDialog && <PromptDialog {...promptDialog} onCancel={() => setPromptDialog(null)} />}

      {confirmDialog && (
        <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />
      )}
    </>
  );
}

type PromptState = {
  title: string;
  description?: string | undefined;
  label: string;
  initialValue?: string | undefined;
  placeholder?: string | undefined;
  submitLabel?: string | undefined;
  destructive?: boolean | undefined;
  onSubmit: (value: string) => Promise<void>;
};

type ConfirmState = {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  destructive?: boolean | undefined;
  requireTyping?: string | undefined;
  onConfirm: () => Promise<void>;
};

type MoveTargetState =
  | { kind: 'folder'; folder: Folder }
  | { kind: 'template'; template: ReportTemplateSummary };

function stripJasperTag(name: string): string {
  return name.replace(/\s*\[Jasper #\d+\]\s*$/, '');
}
